import {
  Badge,
  Button,
  Card,
  Col,
  Dropdown,
  Image,
  List,
  Pagination,
  Row,
  Table,
  Tooltip,
} from "antd";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useHistory } from "react-router-dom/cjs/react-router-dom.min";
import ListEmptyComponent from "../../components/ListEmptyComponent/ListEmptyComponent";
import { useGetBreakpoints } from "../../hooks/useGetBreakpoints";
import axios from "axios";
import { BASE_API_CONFIG } from "../../util/Api";
import { useDispatch, useSelector } from "react-redux";
import { formatDate } from "../../util/helper";
import { ItemDetailMobile } from "../../components/ItemDetailMobile";
import { isArrayNonEmpty } from "../../util/ApiHelper";
import {
  CopyOutlined,
  DeleteOutlined,
  EditOutlined,
  MoreOutlined,
} from "@ant-design/icons";
import { fetchStart, fetchSuccess } from "../../appRedux/actions";

const ViewPolicyDetails = () => {
  const dispatch = useDispatch();
  const history = useHistory();
  const { isMobile } = useGetBreakpoints();
  const [policyList, setPolicyList] = useState({ rows: [], count: 0 });
  const [pageSize, setPageSize] = useState(10);
  const authUser = useSelector(({ auth }) => auth.userData);
  const token = authUser.token;
  const [currentPage, setCurrentPage] = useState(1);
  const cardsPerPage = 4;
  const { Meta } = Card;
  const description = `Please note that the information provided below is for knowledge-sharing purposes only and may change over time. Kindly verify the details independently and proceed accordingly.`;
  const currentCards = useMemo(() => {
    const startIndex = (currentPage - 1) * cardsPerPage;
    return policyList.rows.slice(startIndex, startIndex + cardsPerPage);
  }, [currentPage, policyList.rows]);

  const getRelativeTime = (date) => {
    const inputDate = new Date(date);

    if (isNaN(inputDate.getTime())) {
      return "Invalid date";
    }

    const day = String(inputDate.getDate()).padStart(2, "0");
    const monthNames = [
      "Jan",
      "Feb",
      "Mar",
      "Apr",
      "May",
      "Jun",
      "Jul",
      "Aug",
      "Sep",
      "Oct",
      "Nov",
      "Dec",
    ];
    const month = monthNames[inputDate.getMonth()];
    const year = inputDate.getFullYear();

    return `${day}/${month}/${year}`;
  };

  const fetchPolicyUpdates = useCallback(
    async (page = 1, limit = pageSize) => {
      dispatch(fetchStart());
      try {
        const response = await axios.get(
          `${BASE_API_CONFIG.baseURL}/policyUpdate`,
          {
            params: { page, limit },
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );
        dispatch(fetchStart());
        setPolicyList({
          rows: response.data.rows,
          count: response.data.count,
        });
      } catch (error) {
        console.error("Error fetching policies:", error);
      } finally {
        dispatch(fetchSuccess());
      }
    },
    [pageSize]
  );

  useEffect(() => {
    dispatch(fetchStart());
    fetchPolicyUpdates().then(() =>
      console.log("Data fetched - loader should hide")
    );
  }, [dispatch, fetchPolicyUpdates]);

  const navigateToDetails = () => {
    history.push("policy-updates/details");
  };

  const navigateToViewDetails = useCallback(
    (id) => {
      history.push(`policy-updates/views/${id}`);
    },
    [history]
  );

  function truncateWords(text, maxWords) {
    if (!text) return "";

    const words = text.split(" ");
    if (words.length > maxWords) {
      return words.slice(0, maxWords).join(" ") + "...";
    }
    return text;
  }

    function truncateTitleWords(text, maxWords, maxChars = 22) {
    if (!text) return "";

    const words = text.split(" ");
    let truncated = words.slice(0, maxWords).join(" ");

    if (truncated.length > maxChars) {
      return truncated.slice(0, maxChars) + "...";
    }
    if(words.length > maxWords) {
      return truncated + "...";
    }
    return text;
  }

  const renderPolicyCards = useCallback(
    (data) => {
      return (
        <Row gutter={[16,16]}>
          {data.map((record) => (
            <Col key={record.id} xs={24} sm={12} md={6} lg={6}>
            <Card
              hoverable
              style={{
                width: "100%", 
                display: "flex",
                flexDirection: "column",
                height: "400px",
                margin: 0,
                borderRadius: "16px",
                overflow: "hidden",
              }}
              onClick={() => navigateToViewDetails(record.id)}
              bodyStyle={{ padding: 0, height: "100%" }}
            >
              {record.policyIcon?.[0] ? (
                <div style={{ flex: 1, overflow: "hidden", width: "100%" }}>
                  <Image
                    src={record.policyIcon?.[0]?.src}
                    alt="Policy Document"
                    style={{
                      width: 300,
                      height: 225,
                      objectFit: "cover",
                      display: "block",
                    }}
                    preview={false}
                  />
                </div>
              ) : (
                <div
                  style={{
                    flex: 1,
                    backgroundColor: "#f0f0f0",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    color: "#999",
                    width: 248,
                    height: 225,
                    borderRadius: "16px 16px 0 0",
                  }}
                >
                  No Document Available
                </div>
              )}
              <div
                style={{
                  padding: "12px",
                  position: "relative",
                  minHeight: "200px",
                }}
              >
                <div
                  style={{
                    fontSize: "18px",
                    fontWeight: "bold",
                    marginBottom: "8px",
                    marginTop: "8px",
                  }}
                >
                  <Tooltip
                    title={record.title}
                    placement= "topLeft"
                    color="rgba(0, 0, 0, 0.75)"
                    overlayStyle={{ maxWidth: 200}}
                    mouseEnterDelay={.3}
                    >
                     {truncateTitleWords(record.title, 2)}
                  </Tooltip>
                </div>
                <div
                  style={{
                    fontWeight: 500,
                    fontSize: 14,
                    marginBottom: "8px",
                    position: "relative",
                  }}
                >
                  <strong>Details: </strong>
                  <Tooltip
                    title={record.details}
                    placement="topLeft"
                    color="rgba(0, 0, 0, 0.75)"
                    overlayStyle={{ maxWidth: 200 }}
                    mouseEnterDelay={0.3}
                  >
                    <span> {truncateWords(record.details, 15)} </span>
                  </Tooltip>
                </div>

                <div
                  style={{
                    position: "absolute",
                    bottom: "40px",
                    right: "10px",
                    color: "#A1A7AD",
                  }}
                >
                  <strong>Updated on : </strong>
                  <span>{getRelativeTime(record.createdAt)}</span>
                </div>
              </div>
            </Card>
            </Col>
          ))}
        </Row>
      );
    },
    [navigateToViewDetails]
  );

  return (
    <>
      <Card
        style={{ width: "100%" }}
        title={
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <div className="myexim-table-title">{"Policy Updates"}</div>
          </div>
        }
      >
        <Meta
          description={
            <div style={{ lineHeight: 1.5, marginBottom: "30px" }}>
              <span style={{ color: "#1677ff" }}>{description}</span>
            </div>
          }
        ></Meta>
        {policyList.rows.length > 0 ? (
          <>
            {renderPolicyCards(currentCards)}
            <div
              style={{
                display: "flex",
                justifyContent: "right",
                marginTop: "60px",
              }}
            >
              <Pagination
                current={currentPage}
                total={policyList.count}
                pageSize={cardsPerPage}
                onChange={(page) => setCurrentPage(page)}
                showSizeChanger={false}
              />
            </div>
          </>
        ) : (
          <ListEmptyComponent message="There is no new policy updates" />
        )}
      </Card>
    </>
  );
};

export default ViewPolicyDetails;
